import React, {useState, useEffect} from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import {Field} from 'formik';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import {DndProvider} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend';
import ButtonCommon from '../ButtonCommon';
import FieldImageUpload from '../FieldImageUpload';
import EmptyView from '../Empty';
import ImagePreview from '../ImagePreview';
import fileIcon from '../../assets/file_present-24px.svg';
import camera from '../../assets/camera_alt-24px.svg';
import ok from '../../assets/check-black.svg';
import remove from '../../assets/delete-24px.svg';
import {getFileTypeIcon} from '../../utils/files/getFileTypeIcon';
import styles from './styles.module.scss';

const FieldPasteImage = ({
                           fileUploaded,
                           customIcon = camera,
                           ...rest
                         }) => {
  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  const iconSrc = fileUploaded ? ok : customIcon;

  return (
    <>
      <ButtonCommon
        label={'הוסף'}
        icon={iconSrc}
        onClick={handleShowModal}
        customContainerStyles={styles.buttonOpenModal}
        customIconStyles={styles.iconUpload}
        customLabelStyles={styles.buttonOpenModalLabel}
      />
      <Modal
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={false}
      >
        <DndProvider backend={HTML5Backend}>
          <UploadFile
            handleCloseModal={handleCloseModal}
            {...rest}
          />
        </DndProvider>
      </Modal>
    </>
  )
};

const UploadButton = ({handleChange, field, form, files}) => ({openModal, /*closeModal*/}) => {
  const handleSelect = () => {
    Promise.resolve()
      .then(() => handleChange(field, form))
      .then(() => openModal());
  };

  return (
    <ButtonCommon
      label={'המשך'}
      disabled={files.length === 0}
      customContainerStyles={styles.buttonSubmitContainer}
      onClick={handleSelect}
    />
  );
};

const UploadFile = ({
                      fieldId,
                      fileUploaded,
                      onFileSelected,
                      setFieldValue,
                      handleCloseModal,
                      buttonImageUpload,
                      showOnlyFreeText,
                      formFields,
                      ...rest
                    }) => {
  const [files, setFiles] = useState([]);
  // drag-drop
  const [{canDrop, isOver}, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        if (item) {
          setFiles([item.files[0]])
        }
      },
      canDrop(item) {
        return true
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }
      },
    }),
    [setFiles],
  );
  // copy-paste
  useEffect(() => {
    const paste = (evt) => {
      const clipboardItems = evt.clipboardData.items;
      const items = [].slice.call(clipboardItems)
      if (items.length === 0) {
        return;
      }
      const blob = items[0].getAsFile();
      setFiles([blob]);
    };
    document.addEventListener('paste', paste);
    return () => {
      document.removeEventListener('paste', paste);
    };
  }, []);

  const handleChange = (field, form) => {
    const [selected] = files;
    form.setFieldValue(field.name, selected);
  };

  const onRemoveFile = (fileName) => {
    const newFilesArray = files.filter(item => item.name !== fileName);
    setFiles(newFilesArray);
  };

  const EditableFileName = ({file, index}) => {
    const cleanName = Boolean(file.name)
      ? file.name.split('.')[0]
      : '';
    const extension = Boolean(file.name)
      ? file.name.split('.')[1]
      : '';

    return (
      <input
        type="text"
        defaultValue={cleanName}
        className={styles.fileNameInput}
        onBlur={(e) => {
          const editedFiles = files.map((item, i) => {
            if (index === i) {
              const newName = Boolean(e.target.value)
                ? [e.target.value, extension].join('.')
                : item.name;
              return new File([item], newName, {type: item.type});
            } else return item
          })
          setFiles(editedFiles)
        }}
      />
    )
  };

  return (
    <div className={styles.modalContainer}>
      <h2 className={styles.modalHeader}>{'העלאת קבצים'}</h2>
      <div
        ref={drop}
        className={classnames(styles.dragDropContainer, {[styles.dragDropActive]: canDrop && isOver})}
      >
        <div className={styles.dragDropHeader}>
          <img src={fileIcon} alt="file" className={styles.dragDropIcon}/>
          <div>{'ניתן לגרור קבצים ישירות לכאן או להדביק אותם'}</div>
        </div>
      </div>
      <div className={styles.fileListContainer}>
        {files.filter(Boolean).length > 0 ? (
          <>
            {files.map((file, index) => (
              <div className={styles.fileItem} key={index}>
                <img
                  src={getFileTypeIcon(file.type)}
                  alt={'type'}
                  className={styles.typeIcon}
                />
                <EditableFileName file={file} index={index}/>
                <img
                  src={remove}
                  alt={'remove'}
                  className={styles.removeIcon}
                  onClick={() => {
                    onRemoveFile(file.name)
                  }}
                />
              </div>
            ))}
          </>
        ) : <EmptyView customContainerClassName={styles.empty}/>}
      </div>
      <div className={styles.buttonsContainer}>
        <Field id={fieldId} name={fieldId}>
          {({field, form}) => (
            <ImagePreview
              id={`1-${fieldId}`}
              Button={UploadButton({
                handleChange,
                field,
                form,
                files,
                ...rest
              })}
              image={field.value}
              formInitialValues={{}}
              onConfirm={onFileSelected}
              closeParentModal={handleCloseModal}
              formFields={formFields}
            />
          )}
        </Field>
        <FieldImageUpload
          fieldId={fieldId}
          label="בחירת קובץ"
          labelUploaded="בחירת קובץ"
          icon={camera}
          fileUploaded={fileUploaded}
          onFileSelected={onFileSelected}
          closeParentModal={handleCloseModal}
          formInitialValues={{}}
          customButtonUploadContainer={styles.buttonUploadContainer}
          customButtonLabel={styles.buttonUploadLabel}
          formFields={formFields}
        />
        <ButtonCommon
          label={'סגירה'}
          onClick={handleCloseModal}
          customContainerStyles={styles.buttonCancelContainer}
          customLabelStyles={styles.buttonCancelLabel}
        />
      </div>
    </div>
  );
};

export default FieldPasteImage;